<!-- Base.Chips.vue (client-lms) -->
<template>
  <span class="lms_chip" :class="[chipClasses]">
    <slot></slot>
  </span>
</template>

<script setup lang="ts">
import { computed } from "vue";

const props = defineProps({
  shape: { type: String, default: "square" }, // 'square', 'round'
  type: { type: String, default: "filled" }, // 'filled', 'outlined', 'light'
  color: { type: String, default: "gray" }, // 'gray', 'blue', 'black', 'red', 'green', 'orange', 'yellow'
  size: { type: String, default: "" }, // 'sm', 'lg'
});

const chipClasses = computed(() => [
  `lms_chip_${props.shape}`,
  `lms_chip_${props.type}_${props.color}`,
  props.size ? `lms_chip_${props.size}` : "",
]);
</script>

<style scoped>
.lms_chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  font-family: "Pretendard", sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  white-space: nowrap;
}

/* Shape */
.lms_chip_square { border-radius: 4px; }
.lms_chip_round { border-radius: 100px; }

/* Size */
.lms_chip_sm { padding: 1px 6px; font-size: 11px; }
.lms_chip_lg { padding: 4px 12px; font-size: 14px; }

/* Filled variants */
.lms_chip_filled_gray {
  background-color: var(--lms-grey-100, #F6F7F7);
  color: var(--lms-on-surface-medium-var, rgba(0, 0, 0, 0.5));
}
.lms_chip_filled_blue {
  background-color: var(--lms-blue-50, #EAEDFF);
  color: var(--lms-blue-400, #5C74F6);
}
.lms_chip_filled_black {
  background-color: var(--lms-grey-900, #2A2B2B);
  color: var(--lms-on-primary-high, #FFFFFF);
}
.lms_chip_filled_red {
  background-color: var(--lms-red-400);
  color: #fff;
}
.lms_chip_filled_green {
  background-color: #E8F5E9;
  color: #2E7D32;
}
.lms_chip_filled_orange {
  background-color: #FFF3E0;
  color: #FF9800;
}
.lms_chip_filled_yellow {
  background-color: #FFF8E1;
  color: #B8860B;
}

/* Outlined variants */
.lms_chip_outlined_gray {
  border: 1px solid var(--lms-outline-gray-main, #E0E0E0);
  color: var(--lms-on-surface-medium-var, rgba(0, 0, 0, 0.5));
  background: transparent;
}
.lms_chip_outlined_blue {
  border: 1px solid var(--lms-blue-400, #5C74F6);
  color: var(--lms-blue-400, #5C74F6);
  background: transparent;
}

/* Light variants */
.lms_chip_light_gray {
  background-color: rgba(0, 0, 0, 0.04);
  color: var(--lms-on-surface-medium-var, rgba(0, 0, 0, 0.5));
}
.lms_chip_light_blue {
  background-color: rgba(92, 116, 246, 0.08);
  color: var(--lms-blue-400, #5C74F6);
}
</style>
